import axios from '../../utils/axiosConfig';
import { setReportGenerating, clearReportGenerating } from '../reducers/reportReducers';

/**
 * Security Reports Redux Actions
 * Handles fetching metrics, generating reports, and report history
 */

/**
 * Get security reports metrics for dashboard
 * Can filter by date range and watcher
 */
export const getSecurityReportsMetrics = (formData = null) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		let data;
		if (formData) {
			// POST with filters
			const { data: responseData } = await axios.post(`/api/security-reports/metrics`, formData, config);
			data = responseData;
		} else {
			// GET - defaults to last 7 days
			const { data: responseData } = await axios.get(`/api/security-reports/metrics`, config);
			data = responseData;
		}

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to fetch metrics' };
		}
	} catch (err) {
		console.error('Error fetching security reports metrics:', err);
		return { status: false, msg: 'Error fetching metrics' };
	}
};

/**
 * Generate a new PDF security report
 * Accepts date range and optional watcher filter
 */
export const generateSecurityReport = (formData, currentReportCount = 0) => async (dispatch) => {
	try {
		// Extract report params for tracking
		const reportParams = {
			startDate: formData.get('start_date'),
			endDate: formData.get('end_date'),
			watchers: formData.get('watchers'),
			showSubcategories: formData.get('show_subcategories')
		};

		// Set generating state
		dispatch(setReportGenerating(reportParams, currentReportCount));

		const { data } = await axios.post(`/api/security-reports/generate`, formData);

		if (data && data.status) {
			// Note: We don't clear generating state here - polling will detect completion
			return data;
		} else {
			// Clear generating state on error
			dispatch(clearReportGenerating());
			return { status: false, msg: data?.msg || 'Failed to generate report' };
		}
	} catch (err) {
		console.error('Error generating security report:', err);
		// Clear generating state on error
		dispatch(clearReportGenerating());
		return { status: false, msg: err.response?.data?.msg || err.message || 'Error generating report' };
	}
};

/**
 * Get history of generated reports
 */
export const getReportHistory = () => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.get(`/api/security-reports/history`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to fetch report history' };
		}
	} catch (err) {
		console.error('Error fetching report history:', err);
		return { status: false, msg: 'Error fetching report history' };
	}
};

/**
 * Download a specific report by ID
 * This opens the download in a new window/tab
 */
export const downloadReport = (reportId) => {
	// Open download URL in new window
	const url = `/api/security-reports/download/${reportId}`;
	window.open(url, '_blank');
};


export const getReportSchedules = () => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.get(`/api/security-reports/schedules`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to fetch schedules' };
		}
	} catch (err) {
		console.error('Error fetching report schedules:', err);
		return { status: false, msg: 'Error fetching schedules' };
	}
};

/**
 * Create a new report schedule
 * @param {Object} scheduleData - Schedule configuration
 */
export const createReportSchedule = (scheduleData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/security-reports/schedules`, scheduleData, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to create schedule' };
		}
	} catch (err) {
		console.error('Error creating report schedule:', err);
		return { status: false, msg: err.response?.data?.msg || 'Error creating schedule' };
	}
};

/**
 * Update an existing report schedule
 * @param {Number} scheduleId - ID of the schedule to update
 * @param {Object} scheduleData - Updated schedule configuration
 */
export const updateReportSchedule = (scheduleId, scheduleData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.put(`/api/security-reports/schedules/${scheduleId}`, scheduleData, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to update schedule' };
		}
	} catch (err) {
		console.error('Error updating report schedule:', err);
		return { status: false, msg: err.response?.data?.msg || 'Error updating schedule' };
	}
};

/**
 * Delete a report schedule
 * @param {Number} scheduleId - ID of the schedule to delete
 */
export const deleteReportSchedule = (scheduleId) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.delete(`/api/security-reports/schedules/${scheduleId}`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to delete schedule' };
		}
	} catch (err) {
		console.error('Error deleting report schedule:', err);
		return { status: false, msg: err.response?.data?.msg || 'Error deleting schedule' };
	}
};

/**
 * Toggle a report schedule active/inactive
 * @param {Number} scheduleId - ID of the schedule to toggle
 */
export const toggleReportSchedule = (scheduleId) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.patch(`/api/security-reports/schedules/${scheduleId}/toggle`, {}, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to toggle schedule' };
		}
	} catch (err) {
		console.error('Error toggling report schedule:', err);
		return { status: false, msg: err.response?.data?.msg || 'Error toggling schedule' };
	}
};

